import { jsx } from 'react/jsx-runtime';
import { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { buttonVariants, Button } from './button.js';
import { cn } from '../../lib/utils.js';
import { LineIcon } from './icons/LineIcon.js';
import { getDefaultClassNames } from '../../vendor/react-day-picker/dist/esm/helpers/getDefaultClassNames.js';
import { DayPicker } from '../../vendor/react-day-picker/dist/esm/DayPicker.js';
import { ko } from '../../vendor/date-fns/locale/ko.js';
import { enUS } from '../../vendor/date-fns/locale/en-US.js';

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "label",
  buttonVariant = "ghost",
  formatters,
  components,
  // locale,
  ...props
}) {
  const defaultClassNames = getDefaultClassNames();
  const { i18n } = useTranslation();
  const localeMap = i18n.language === "ko" ? "ko-KR" : "en-US";
  const yearFormatter = new Intl.DateTimeFormat(localeMap, {
    year: "numeric"
  });
  const monthFormatter = new Intl.DateTimeFormat(localeMap, {
    month: "short"
  });
  return /* @__PURE__ */ jsx("div", { className: "flex justify-center w-full calendar-wrap", children: /* @__PURE__ */ jsx(
    DayPicker,
    {
      showOutsideDays,
      className: cn(
        "w-full bg-background group/calendar p-3 [--cell-size:24px] [[data-slot=card-content]_&]:bg-transparent [[data-slot=popover-content]_&]:bg-transparent",
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className
      ),
      captionLayout,
      formatters: {
        formatMonthDropdown: (date) => monthFormatter.format(date),
        formatYearDropdown: (date) => yearFormatter.format(date),
        ...formatters
      },
      classNames: {
        root: cn("!p-0 mx-auto", defaultClassNames.root),
        months: cn(
          "relative flex flex-col gap-4 md:flex-row",
          defaultClassNames.months
        ),
        month: cn("flex w-full flex-col gap-4", defaultClassNames.month),
        nav: cn(
          "absolute inset-x-0 top-0 left-3 right-3 h-[42px] flex md:w-[calc(100%-24px)] items-center justify-end gap-5",
          defaultClassNames.nav
        ),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          "h-[--cell-size] w-[--cell-size] select-none p-0 aria-disabled:opacity-50 rounded-[4px]",
          defaultClassNames.button_previous
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          "h-[--cell-size] w-[--cell-size] select-none p-0 aria-disabled:opacity-50 rounded-[4px]",
          defaultClassNames.button_next
        ),
        month_caption: cn(
          "flex h-[42px] w-full pl-1 pr-3 items-center justify-start",
          defaultClassNames.month_caption
        ),
        dropdowns: cn(
          "flex h-[42px] w-full items-center justify-start text-sm font-weight-medium",
          defaultClassNames.dropdowns
        ),
        dropdown_root: cn(
          "p-0 has-focus:border-ring border-transparent focus-within:border-primary-03 focus-within:border-[2px] has-focus:ring-ring/50 has-focus:ring-[3px] relative rounded-[8px] border",
          defaultClassNames.dropdown_root
        ),
        dropdown: cn(
          "bg-popover absolute inset-0 opacity-0",
          defaultClassNames.dropdown
        ),
        caption_label: cn(
          "select-none font-weight-medium heading-h5 text-basic-02 font-weight-bold",
          captionLayout !== "label" && "[&>svg]:text-basic-01 flex items-center gap-1 py-2 pl-2 pr-1 font-weight-bold",
          defaultClassNames.caption_label
        ),
        table: "w-full border-collapse",
        weekdays: cn("flex ml-auto justify-between", defaultClassNames.weekdays),
        weekday: cn(
          "body-md font-weight-medium text-basic-05 select-none rounded-md w-[42px] h-[34px] !leading-[34px] p-0",
          defaultClassNames.weekday
        ),
        week: cn("flex w-full ml-auto justify-between", defaultClassNames.week),
        week_number_header: cn(
          "w-[--cell-size] select-none",
          defaultClassNames.week_number_header
        ),
        week_number: cn(
          "text-basic-05 select-none text-[0.8rem]",
          defaultClassNames.week_number
        ),
        day: cn(
          "group/day relative w-[42px] h-[46px] flex-0 px-1 rounded-[10px] inline-flex justify-center items-center select-none p-0 text-center [&:first-child[data-selected=true]_button]:rounded-l-md [&:last-child[data-selected=true]_button]:rounded-r-md body-sm",
          defaultClassNames.day
        ),
        range_start: cn("bg-accent rounded-l-md", defaultClassNames.range_start),
        range_middle: cn("rounded-none", defaultClassNames.range_middle),
        range_end: cn("bg-accent rounded-r-md", defaultClassNames.range_end),
        today: cn(
          "bg-accent text-accent-foreground rounded-md data-[selected=true]:rounded-none",
          defaultClassNames.today
        ),
        outside: cn(
          "text-basic-05 aria-selected:text-basic-05",
          defaultClassNames.outside
        ),
        disabled: cn("text-basic-05 opacity-50", defaultClassNames.disabled),
        hidden: cn("invisible", defaultClassNames.hidden),
        ...classNames
      },
      components: {
        Root: ({ className: className2, rootRef, ...props2 }) => {
          return /* @__PURE__ */ jsx(
            "div",
            {
              "data-slot": "calendar",
              ref: rootRef,
              className: cn(className2),
              ...props2
            }
          );
        },
        Chevron: ({ orientation, ...props2 }) => {
          if (orientation === "left") {
            return /* @__PURE__ */ jsx(
              LineIcon,
              {
                icoColor: "bg-icon-basic-01",
                icoName: "ico-caret-left",
                ...props2
              }
            );
          }
          if (orientation === "right") {
            return /* @__PURE__ */ jsx(
              LineIcon,
              {
                icoColor: "bg-icon-basic-01",
                icoName: "ico-caret-right",
                ...props2
              }
            );
          }
          return /* @__PURE__ */ jsx("div", { className: "w-4 h-4 ml-auto -rotate-90 z-[-1]", children: /* @__PURE__ */ jsx(
            LineIcon,
            {
              icoColor: "bg-icon-basic-01",
              icoName: "ico-caret-left",
              type: "xs",
              ...props2
            }
          ) });
        },
        DayButton: CalendarDayButton,
        WeekNumber: ({ children, ...props2 }) => {
          return /* @__PURE__ */ jsx("td", { ...props2, children: /* @__PURE__ */ jsx("div", { className: "flex size-[--cell-size] items-center justify-center text-center", children }) });
        },
        ...components
      },
      locale: i18n.language === "ko" ? ko : enUS,
      ...props
    }
  ) });
}
function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}) {
  const defaultClassNames = getDefaultClassNames();
  const ref = useRef(null);
  useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);
  return /* @__PURE__ */ jsx(
    Button,
    {
      ref,
      variant: "ghost",
      size: "icon",
      "data-day": day.date.toLocaleDateString(),
      "data-selected-single": modifiers.selected && !modifiers.range_start && !modifiers.range_end && !modifiers.range_middle,
      "data-range-start": modifiers.range_start,
      "data-range-end": modifiers.range_end,
      "data-range-middle": modifiers.range_middle,
      className: cn(
        `relative z-1 w-[40px] h-[40px] font-weight-medium text-primary-01 !rounded-[10px] !ring-0 !shadow-none 
                data-[selected-single=true]:bg-interactive-active-selected-03 data-[selected-single=true]:text-interactive-active-selected-03
                data-[range-middle=true]:bg-interactive-active-selected-03 data-[range-middle=true]:text-primary-04
                data-[range-start=true]:bg-interactive-active-selected-03 data-[range-start=true]:text-interactive-active-selected-03
                data-[range-end=true]:bg-interactive-active-selected-03 data-[range-end=true]:text-interactive-active-selected-03flex aspect-[1/1.2] 
                min-w-[--cell-size] flex-col gap-1 leading-none data-[range-end=true]:rounded-r-full data-[range-middle=true]:rounded-none 
                data-[range-start=true]:rounded-l-full [&>span]:text-xs`,
        defaultClassNames.day,
        className
      ),
      ...props
    }
  );
}

export { Calendar, CalendarDayButton };
